import React from 'react';
import { DetailsHeader } from './DetailsHeader';
import { SharedNotesTimeline } from './SharedNotesTimeline';
import { ValueLabel } from '@shared/components/ValueLabel';
import { Wrench, CreditCard, Calendar, Clock } from 'lucide-react';
import { useRequestDetail } from '../../../shared/hook';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useTranslation } from 'react-i18next';

export const ServiceDetails = ({ data }: { data: any }) => {
  const { t } = useTranslation();
  const { remoteUtilityId } = getLoginDataFromStorage();
  const { data: requestDetail, isLoading } = useRequestDetail({
    remote_utility_id: remoteUtilityId,
    id: data?.id,
  });

  const details = requestDetail?.result || data;

  if (isLoading) return <div className="p-6 text-center text-slate-500">{t("Loading...")}</div>;

  return (
    <div className="flex flex-col h-full bg-slate-50/50 pb-6">
      <DetailsHeader 
        icon={<Wrench size={18} />}
        iconBgColor="bg-blue-100"
        iconTextColor="text-blue-600"
        title={details?.utilitySupportRequest?.name || t("Service Request")}
        status={details?.statusDisplay}
        id={details?.requestNo ?? t("N/A")}
        submittedDate={details?.createdDate ?? t("N/A")}
      />
      <div className="p-6 space-y-4">
        {/* SERVICE DETAILS & PAYMENT */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><Wrench size={16}/> {t("Service Details")}</h4>
            <div className="space-y-4">
              <ValueLabel label={t("Service Type")} value={details?.utilitySupportRequest?.supportRequestType || t("N/A")} />
              <ValueLabel label={t("Submitted")} value={details?.createdDate ? new Date(details.createdDate).toLocaleDateString() : t("N/A")} />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><CreditCard size={16}/> {t("Payment")}</h4>
            <div className="space-y-4">
              <ValueLabel label={t("Service Charges")} value="$75.00" />
              <ValueLabel label={t("Payment Method")} value={t("Online Payment")} />
            </div>
          </div>
        </div>

        {/* SCHEDULED APPOINTMENT */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><Calendar size={16}/> {t("Scheduled Appointment")}</h4>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="border border-gray-200 rounded-lg p-3 flex items-center gap-3">
              <div className="p-2 bg-blue-50 text-blue-600 rounded"><Calendar size={16}/></div>
              <div>
                <div className="text-xs text-gray-500 font-medium mb-0.5">{t("Preferred Date")}</div>
                <div className="font-bold text-gray-900 text-sm">{details?.requestDate ? new Date(details.requestDate).toLocaleDateString() : t("N/A")}</div>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 flex items-center gap-3">
              <div className="p-2 bg-blue-50 text-blue-600 rounded"><Clock size={16}/></div>
              <div>
                <div className="text-xs text-gray-500 font-medium mb-0.5">{t("Preferred Time")}</div>
                <div className="font-bold text-gray-900 text-sm">{t("N/A")}</div>
              </div>
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-3">
            <ValueLabel label={t("Additional Instructions")} value={details?.utilitySupportRequest?.longDescription || t("No additional instructions provided.")} />
          </div>
        </div>

        {/* TABS */}
        <SharedNotesTimeline data={data} />
      </div>
    </div>
  );
};
